import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";

import { DeviceInventoryService, BLEService } from "../services";
import config from "config";

const BLEScanModal = ({ showScanModal, onComplete }) => {
    const [loading, setLoading] = useState(true);
    const [availableInventory, setAvailableInventory] = useState(null);
    const [ssid, setSSID] = useState("");
    const [wifiPassword, setWifiPassword] = useState("");
    const [needExtraInfo, setNeedExtraInfo] = useState(false);
    const [scanDone, setScanDone] = useState(false);
    const [foundInventory, setFoundInventory] = useState(null);
    const [bleContext, setBLEContext] = useState(null);
    const [configuring, setConfiguring] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);
    const [retry, setRetry] = useState(false);
    const [applying, setApplying] = useState(false);
    const [caCertStatus, setCACertStatus] = useState(false);
    const [clientCertStatus, setClientCertStatus] = useState(false);
    const [clientKeyStatus, setClientKeyStatus] = useState(false);
    const [wifiStatus, setWifiStatus] = useState(false);
    const [opsenseConnectionStatus, setOpsenseConnectionStatus] = useState(false);

    useEffect(() => {
        setLoading(true);
        DeviceInventoryService.getAvailableInventory("ble")
            .then(({ availableInventory }) => {
                setAvailableInventory(availableInventory);
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
            });
    }, []);

    const handleScanStatusUpdates = (message) => {
        setStatusMessage(message);
    };

    const handleStateChanges = (name, state, changeType) => {
        if (changeType == 'notify') {
            console.log(`notify ${name}=${state[name]}`);
            switch(name) {
                case 'mqtt_ca_cert': {
                    setCACertStatus(state[name] == "VALID");
                } break;
                case 'mqtt_client_cert': {
                    setClientCertStatus(state[name] == "VALID");
                } break;
                case 'mqtt_client_key': {
                    setClientKeyStatus(state[name] == 'VALID');
                } break;
                case 'wifi_status': {
                    setWifiStatus(state[name]);
                } break;
                case 'wifi_error': {
                    if (state[name] != 0) {
                        console.error(`WiFi error ${state[name]}`);
                    }
                } break;
                case 'mqtt_status': {
                    setOpsenseConnectionStatus(state[name]);
                } break;
                case 'mqtt_error': {
                    if (state[name] != 0) {
                        console.error(`MQTT error ${state[name]}`);
                    }
                } break;
            }
        }
    };

    const handleDisconnect = () => {
        console.log(`Disconnect retry=${retry} opsenseConnectionStatus=${opsenseConnectionStatus}`);
        
        if (!retry) {
            setStatusMessage("Device disconnected.");
        }

        setApplying(false);

        if (!opsenseConnectionStatus) {
            setRetry(true);
        } else {
            cleanupAndHide(bleContext, true);
        }
    };

    const reset = () => {
        setLoading(false);
        setSSID("");
        setWifiPassword("");
        setNeedExtraInfo(false);
        setScanDone(false);
        setFoundInventory(null);
        setBLEContext(null);
        setConfiguring(false);
        setStatusMessage(null);
        setApplying(false);
        setCACertStatus(false);
        setClientCertStatus(false);
        setClientKeyStatus(false);
        setWifiStatus(false);
        setOpsenseConnectionStatus(false);
    }

    const attemptRetry = () => {
        setRetry(false);

        if (bleContext && bleContext.isConnected()) {
            applyConfiguration();
        } else {
            reset();
            startScan();
        }
    };

    const startScan = () => {
        setStatusMessage("Scanning...");

        BLEService.startScan(handleScanStatusUpdates, handleStateChanges, handleDisconnect)
            .then(({ bleContext }) => {
                var found = false;
                for (const inventoryItem of availableInventory) {
                    //console.log(`Test ${inventoryItem.deviceRadioMacAddress} == ${bleContext.state['device_id']}`);
                    if (inventoryItem.deviceRadioMacAddress == bleContext.state['device_id']) {
                        setStatusMessage("Device found in inventory.");

                        setScanDone(true);

                        setNeedExtraInfo(inventoryItem.extendedConfigurationData["wifi_ssid"] == null && inventoryItem.extendedConfigurationData["wifi_password"] == null);

                        setBLEContext(bleContext);
                        setFoundInventory(inventoryItem);
                        
                        found = true;
                        break;
                    }
                }

                if (!found) {
                    setStatusMessage("Unable to find a matching inventory record for the paired device.");
                    setConfiguring(false);
                }
            })
            .catch((err) => {
                console.error(err);
                setStatusMessage("Error scanning for device");
                setConfiguring(false);
            });
    };

    useEffect(() => {
        if (!needExtraInfo && foundInventory) {
            applyConfiguration();
        }
    }, [needExtraInfo, foundInventory]);

    const applyConfiguration = () => {
        if (applying) {
            console.error("applyConfiguration called multiple times.");
            return;
        }

        setConfiguring(true);
        setNeedExtraInfo(false);
        setApplying(true);

        //console.log(foundInventory);
        //console.log(foundInventory.extendedConfigurationData);

        setStatusMessage("Applying device configuration.");

        bleContext.writeValues = {
            wifi_ssid: foundInventory.extendedConfigurationData["wifi_ssid"] ?? ssid,
            wifi_password: foundInventory.extendedConfigurationData["wifi_password"] ?? wifiPassword,
            mqtt_broker_url: config.MQTT_URL,
            mqtt_broker_port: "8883",
            mqtt_ca_cert: foundInventory.extendedConfigurationData["ca_crt"],
            mqtt_client_cert: foundInventory.extendedConfigurationData["mqtt_crt"],
            mqtt_client_key: foundInventory.extendedConfigurationData["mqtt_key"],
        };

        BLEService.applyConfiguration(bleContext)
                  .then(() => {
                      console.log('configuration complete');
                      setStatusMessage("Device configuration complete.");
                      cleanupAndHide(bleContext, true);
                  })
                  .catch((err) => {
                      console.error(err);
                      setStatusMessage(`Error applying device configuration: ${err}`);

                      setConfiguring(false);
                      setApplying(false);
                      setRetry(true);

                      if (bleContext.isConnected()) {
                        //console.log("Running context cleanup.")
                        bleContext.cleanup();
                      }
                  });
    };

    const cleanupAndHide = (context, success) => {
        reset();
        setRetry(false);

        //console.log(context);

        if (context != null) {
            context.cleanup();
        }

        onComplete(success ? foundInventory?.deviceRadioMacAddress : null);
    };

    return (
        <Modal show={showScanModal} onHide={() => { cleanupAndHide(null, false); }} className="ble-scan-modal">
            <Modal.Header closeButton>
                <Modal.Title>Device Scan</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div>Loading...</div>
                ) : (
                    <div>
                        {(!availableInventory || availableInventory.lenth == 0) && <div>No inventory available.</div>}

                        {!scanDone && (
                            <div>
                                <ul className="list-inline clearfix">
                                    <li style={{ width: "50%" }}>
                                        <b>Press and hold the SEND button for 7 seconds</b> to put the device into pairing mode. Once in pairing mode use the "Scan For Device" button to continue the
                                        configuration. <br /> <br />
                                        <Button onClick={startScan} className="btn btn-primary btn-block">
                                            Scan For Device
                                        </Button>
                                    </li>
                                    <li>
                                        <img height={250} style={{ paddingLeft: 40 }} src={`${config.WEB_BASE_URL}/images/node-need-setup.png`} />
                                    </li>
                                </ul>
                            </div>
                        )}

                        {needExtraInfo && (
                            <div className="login-form">
                                <form>
                                    <table className="table table-condensed">
                                        <tbody>
                                            <tr>
                                                <td>SSID:</td>
                                                <td>
                                                    <input value={ssid} onChange={(e) => setSSID(e.target.value)} type="text" id="wifissid" name="wifissid" autoComplete="new-password" />
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>Password:</td>
                                                <td>
                                                    <input
                                                        value={wifiPassword}
                                                        onChange={(e) => setWifiPassword(e.target.value)}
                                                        type="password"
                                                        id="wifikey"
                                                        name="wifikey"
                                                        autoComplete="new-password"
                                                    />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </form>
                            </div>
                        )}

                        {statusMessage && <div>{statusMessage}</div>}
                        {applying && 
                        <div style={{paddingTop:10}}>
                            Security Configuration: {caCertStatus && clientCertStatus && clientKeyStatus ? <b>Ready</b> : <span>Waiting</span>} <br/>
                            WiFi Connection: {wifiStatus ? <b>Ready</b> : <span>Waiting</span>} <br/>
                            OpSense Connection: {opsenseConnectionStatus ? <b>Ready</b> : <span>Waiting</span>} <br/>
                        </div>}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {!configuring && !applying && !retry && scanDone && <Button onClick={applyConfiguration}>Apply Configuration</Button>}
                {retry && <Button onClick={attemptRetry}>Retry</Button>}
            </Modal.Footer>
        </Modal>
    );
};

export default BLEScanModal;